<template>
  <div class="relative"
       v-on-clickaway="hide">
    <input class="form-input w-full"
           :class="{ 'error': error }"
           autocomplete="off"
           v-bind="$attrs"
           :name="name"
           :type="type"
           :min="MIN_DATE"
           :max="MAX_DATE"
           :value="displayValue"
           :title="title"
           ref="input"
           @change="onChange"
           @focus="show"
           @click="show"
           @keydown.esc="hide"
           @keydown.enter.prevent="onChange"
           @keydown.left.prevent="addDays(-1)"
           @keydown.right.prevent="addDays(1)"
           @keydown.up.prevent="addDays(-7)"
           @keydown.down.prevent="addDays(7)"
           @keydown.tab="hide">
    <transition name="dropdown-fade">
      <div class="absolute z-40 bg-white min-w-full rounded shadow-lg p-2"
           v-if="open">
        <div class="flex items-stretch justify-between py-1">
          <button @click="previousMonth"
                  type="button"
                  tabindex="-1"
                  class="p-3 border border-gray-300 mx-px focus:border-blue-500 focus:shadow-outline">
            <BaseIcon name="chevron-left"
                      size="16" />
          </button>
          <select class="form-select flex-1 mx-px"
                  tabindex="-1"
                  @change="setCurrentMonth">
            <option v-for="(month, index) in months"
                    :key="month"
                    :value="index"
                    :selected="index === currentMonth">{{ month }}</option>
          </select>
          <select class="form-select flex-1 mx-px"
                  tabindex="-1"
                  @change="setCurrentYear">
            <option v-for="year in years"
                    :key="year"
                    :value="year"
                    :selected="year === currentYear">{{ year }}</option>
          </select>
          <button @click="nextMonth"
                  type="button"
                  tabindex="-1"
                  class="p-3 border border-gray-300 mx-px focus:border-blue-500 focus:shadow-outline">
            <BaseIcon name="chevron-right"
                      size="16" />
          </button>
        </div>
        <div class="flex text-center justify-around font-semibold">
          <div v-for="weekDay in weekDays"
               :key="weekDay"
               class="p-2">
            {{ weekDay }}
          </div>
        </div>
        <div v-for="week in weeks"
             :key="week.nr"
             class="flex text-center">
          <button v-for="day in week.days"
                  :key="day.date"
                  type="button"
                  tabindex="-1"
                  class="flex-1 p-2 hover:bg-gray-200 hover:text-gray-900 rounded-sm"
                  :class="{ 'text-gray-400': !day.active, 'bg-blue-500 text-white': value === day.date, 'font-semibold bg-gray-100': today === day.date }"
                  @click="pick(day.date)">
            {{ day.day }}
          </button>
        </div>
      </div>
    </transition>
    <div class="form-error"
         v-if="!open && error">{{ error }}</div>
  </div>
</template>

<script>
import { mixin as clickaway } from "vue-clickaway";
import dayjs from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
import { isMobile } from "@/functions.js";

dayjs.extend(weekOfYear);

const MOBILE = isMobile.any();

const MIN_DATE = "2000-01-01";
const MAX_DATE = "2050-01-01";

const MONTHS = [
  "januari",
  "februari",
  "maart",
  "april",
  "mei",
  "juni",
  "juli",
  "augustus",
  "september",
  "oktober",
  "november",
  "december"
];

export default {
  inheritAttrs: false,

  mixins: [clickaway],

  props: {
    name: {
      type: String,
      required: true
    },
    value: {
      required: true
    },
    title: {
      default: ""
    },
    error: {
      type: [String, Boolean],
      default: false
    }
  },

  data() {
    return {
      open: false,
      current: this.value ? dayjs(this.value) : dayjs(),
      MIN_DATE,
      MAX_DATE
    };
  },

  watch: {
    value() {
      this.current = this.value ? dayjs(this.value) : dayjs();
    }
  },

  computed: {
    type() {
      return MOBILE ? "date" : "text";
    },

    displayValue() {
      if (MOBILE) return (this.value || "").substring(0, 10);

      if (this.value) return dayjs(this.value).format("DD-MM-YYYY");

      return "";
    },

    today() {
      return dayjs().format("YYYY-MM-DD");
    },

    months() {
      return MONTHS;
    },

    currentMonth() {
      return this.current.month();
    },

    currentYear() {
      return this.current.year();
    },

    years() {
      let year = this.currentYear;

      return [
        year - 4,
        year - 3,
        year - 2,
        year - 1,
        year,
        year + 1,
        year + 2,
        year + 3,
        year + 4
      ];
    },

    weekDays() {
      return ["ma", "di", "wo", "do", "vr", "za", "zo"];
    },

    weeks() {
      let weeks = [];

      let startOfMonth = this.current.startOf("month");
      let firstDay = startOfMonth.set("day", 1);
      let monday = firstDay.clone();

      do {
        let days = [0, 1, 2, 3, 4, 5, 6].map(value => {
          let day = monday.add(value, "day");
          return {
            date: day.format("YYYY-MM-DD"),
            day: day.date(),
            active: day.isSame(startOfMonth, "month")
          };
        });
        weeks.push({ nr: monday.week(), days });
        monday = monday.add(1, "week");
      } while (monday.isSame(startOfMonth, "month"));

      return weeks;
    }
  },

  methods: {
    show() {
      if (MOBILE) return;

      this.open = true;
    },

    hide() {
      this.current = this.value ? dayjs(this.value) : dayjs();
      this.open = false;
    },

    focusInput() {
      this.$refs.input.focus();
    },

    previousMonth() {
      this.current = this.current.subtract(1, "month");
    },

    nextMonth() {
      this.current = this.current.add(1, "month");
    },

    setCurrentMonth(event) {
      this.current = this.current.month(parseInt(event.target.value));
      this.focusInput();
    },

    setCurrentYear(event) {
      this.current = this.current.year(parseInt(event.target.value));
      this.focusInput();
    },

    addDays(days) {
      if (MOBILE) return;

      this.open = true;

      if (this.value) {
        let date = dayjs(this.value).add(days, "day");
        this.$emit("input", date.format("YYYY-MM-DD"));
      } else {
        this.$emit("input", this.current.format("YYYY-MM-DD"));
      }
    },

    parseInputDate(dateString) {
      if (!dateString) return null;

      if (MOBILE) return dateString;

      const parts = dateString.includes("-")
        ? dateString.split("-")
        : dateString.match(/(\d{2})/g);

      if (parts.length === 3) {
        let day = parseInt(parts[0], 10);
        let month = parseInt(parts[1], 10) - 1;
        let year = parseInt(parts[2], 10);

        if (year < 100 && year > 9) year += 2000;

        return dayjs(new Date(year, month, day));
      }
    },

    onChange(event) {
      let date = this.parseInputDate(event.target.value);

      if (date) {
        this.pick(date);
      } else {
        this.$emit("input", null);
      }
    },

    pick(date) {
      if (typeof date === "string") {
        date = dayjs(date);
      }

      if (date.isBefore(dayjs(MIN_DATE))) date = dayjs(MIN_DATE);

      if (date.isAfter(dayjs(MAX_DATE))) date = dayjs(MAX_DATE);

      this.$emit("input", date.format("YYYY-MM-DD"));

      this.hide();
    }
  }
};
</script>
